// import TagManager from 'react-gtm-module';
import TagManager from 'react-gtm-module';

interface AnalyticsEvent {
  event: string;
  category?: string;
  action?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

export const trackEvent = (eventData: AnalyticsEvent) => {
  TagManager.dataLayer({
    dataLayer: {
      ...eventData,
    },
  });
};

// Specific tracking functions
export const trackLogin = (method: 'email' | 'google' | 'facebook', userId?: string) => {
  trackEvent({
    event: 'user_login',
    category: 'Authentication',
    action: 'Login',
    label: method,
    user_id: userId,
    login_method: method,
  });
};

export const trackMenuAccess = (menuCategory: string, page: string) => {
  trackEvent({
    event: 'menu_access',
    category: 'Navigation',
    action: 'Menu Access',
    label: menuCategory,
    page_location: page,
    menu_category: menuCategory,
  });
};

export const trackAddToCart = (itemId: string, itemName: string, price: number, quantity: number) => {
  trackEvent({
    event: 'add_to_cart',
    category: 'Ecommerce',
    action: 'Add to Cart',
    label: itemName,
    value: price * quantity,
    currency: 'USD',
    items: [{
      item_id: itemId,
      item_name: itemName,
      price: price,
      quantity: quantity,
    }],
  });
};

// export const trackCheckoutStart = (cartValue: number, itemCount: number) => {
//   trackEvent({
//     event: 'begin_checkout',
//     category: 'Ecommerce',
//     action: 'Checkout Started',
//     value: cartValue,
//     currency: 'USD',
//     item_count: itemCount,
//   });
// };

export const trackPromotionView = (promotionId: string, promotionName: string) => {
  trackEvent({
    event: 'view_promotion',
    category: 'Promotions',
    action: 'View Promotion',
    label: promotionName,
    promotion_id: promotionId,
    promotion_name: promotionName,
  });
};

// ...existing code...

export const trackOrderSuccess = (orderData: {
  orderId: string;
  totalValue: number;
  paymentMethod: string;
  itemCount: number;
  items: any[];
  customerId?: string;
  walletPaymentAmount?: number;
}) => {
  trackEvent({
    event: 'purchase',
    category: 'Ecommerce',
    action: 'Order Success',
    label: `Order ${orderData.orderId}`,
    transaction_id: orderData.orderId,
    value: orderData.totalValue,
    currency: 'LKR',
    payment_method: orderData.paymentMethod,
    customer_id: orderData.customerId,
    item_count: orderData.itemCount,
    wallet_payment_amount: orderData.walletPaymentAmount || 0,
    items: orderData.items.map(item => ({
      item_id: item.menuItemId || item.id,
      item_name: item.displayName || item.name,
      item_category: item.itemType || 'MenuItem',
      price: item.unitPrice || item.price,
      quantity: item.quantity,
      portion_size: item.portionSize
    }))
  });
};

export const trackCheckoutStart = (checkoutData: {
  cartValue: number;
  itemCount: number;
  paymentMethod: string;
  customerId?: string;
  useAppCash: boolean;
  walletBalance?: number;
}) => {
  trackEvent({
    event: 'begin_checkout',
    category: 'Ecommerce',
    action: 'Checkout Started',
    value: checkoutData.cartValue,
    currency: 'LKR',
    payment_method: checkoutData.paymentMethod,
    customer_id: checkoutData.customerId,
    item_count: checkoutData.itemCount,
    use_app_cash: checkoutData.useAppCash,
    wallet_balance: checkoutData.walletBalance || 0,
  });
};

// New tracking functions for additional user interactions

export const trackSignUp = (method: 'email' | 'google', userId?: string) => {
  trackEvent({
    event: 'sign_up',
    category: 'Authentication',
    action: 'Sign Up',
    label: method,
    user_id: userId,
    signup_method: method,
  });
};

export const trackPageVisit = (pageName: string, pageUrl?: string) => {
  trackEvent({
    event: 'page_view',
    category: 'Navigation',
    action: 'Page Visit',
    label: pageName,
    page_location: pageUrl || window.location.pathname,
    page_title: document.title,
  });
};

export const trackCartVisit = (cartItemCount: number, cartValue: number) => {
  trackEvent({
    event: 'view_cart',
    category: 'Ecommerce',
    action: 'Cart Visit',
    value: cartValue,
    currency: 'LKR',
    item_count: cartItemCount,
  });
};

export const trackCartDetails = (action: 'view_details' | 'modify_cart', details: {
  itemCount: number;
  totalValue: number;
  cartId?: string;
}) => {
  trackEvent({
    event: 'cart_interaction',
    category: 'Ecommerce',
    action: action,
    value: details.totalValue,
    currency: 'LKR',
    item_count: details.itemCount,
    cart_id: details.cartId,
  });
};

export const trackRemoveFromCart = (itemId: string, itemName: string, price: number, quantity: number) => {
  trackEvent({
    event: 'remove_from_cart',
    category: 'Ecommerce',
    action: 'Remove from Cart',
    label: itemName,
    value: price * quantity,
    currency: 'LKR',
    items: [{
      item_id: itemId,
      item_name: itemName,
      price: price,
      quantity: quantity,
    }],
  });
};

export const trackPaymentSelection = (paymentMethod: string, orderValue: number, useWallet?: boolean) => {
  trackEvent({
    event: 'payment_method_selected',
    category: 'Ecommerce',
    action: 'Payment Method Selection',
    label: paymentMethod,
    value: orderValue,
    currency: 'LKR',
    payment_method: paymentMethod,
    use_wallet: useWallet || false,
  });
};

export const trackOrderPlacement = (orderData: {
  orderId: string;
  orderValue: number;
  paymentMethod: string;
  itemCount: number;
  orderType: 'Delivery' | 'Pickup';
  customerId?: string;
}) => {
  trackEvent({
    event: 'place_order',
    category: 'Ecommerce',
    action: 'Order Placement',
    label: `Order ${orderData.orderId}`,
    transaction_id: orderData.orderId,
    value: orderData.orderValue,
    currency: 'LKR',
    payment_method: orderData.paymentMethod,
    item_count: orderData.itemCount,
    order_type: orderData.orderType,
    customer_id: orderData.customerId,
  });
};

export const trackOrderStatus = (status: 'success' | 'cancelled' | 'failed', orderData: {
  orderId: string;
  orderValue: number;
  paymentMethod: string;
  errorReason?: string;
}) => {
  trackEvent({
    event: `order_${status}`,
    category: 'Ecommerce',
    action: `Order ${status.charAt(0).toUpperCase() + status.slice(1)}`,
    label: `Order ${orderData.orderId}`,
    transaction_id: orderData.orderId,
    value: orderData.orderValue,
    currency: 'LKR',
    payment_method: orderData.paymentMethod,
    ...(status === 'failed' && orderData.errorReason && { error_reason: orderData.errorReason }),
  });
};

export const trackPaymentMethodChange = (fromMethod: string, toMethod: string, orderValue: number) => {
  trackEvent({
    event: 'payment_method_changed',
    category: 'Ecommerce',
    action: 'Payment Method Change',
    label: `${fromMethod} to ${toMethod}`,
    value: orderValue,
    currency: 'LKR',
    from_method: fromMethod,
    to_method: toMethod,
  });
};